import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, BadgeDollarSign, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => { if (!d) return '—'; const [y, m, day] = String(d).slice(0, 10).split('-'); return `${m}/${day}/${y}`; };
const sum = (list) => (Array.isArray(list) ? list : []).reduce((s, b) => s + (Number(b?.valor) || 0), 0);

// Dois tipos de ajuste no relatório salvo: backcharge (desconta) e adicional (acrescenta)
const KINDS = {
  backcharge: { field: 'backcharges', sign: -1, icon: '⚠️', pt: 'Backcharge', en: 'Backcharge', Icon: AlertTriangle, tone: 'yellow', btn: 'bg-yellow-600 hover:bg-yellow-700', placeholderPt: 'Ex: Dano no material, retrabalho janela #3...', placeholderEn: 'E.g. Damaged material, rework window #3...' },
  adicional: { field: 'adicionais', sign: 1, icon: '💰', pt: 'Adicional', en: 'Additional', Icon: BadgeDollarSign, tone: 'green', btn: 'bg-green-600 hover:bg-green-700', placeholderPt: 'Ex: Bônus por desempenho, serviço extra...', placeholderEn: 'E.g. Performance bonus, extra service...' },
};

// Equipes do relatório com Valor Final bruto (soma de calculated_value dos entries encontrados)
const teamsOf = (report, adj) => {
  const map = {};
  (report?.entries || []).forEach((e) => {
    if (!e.found_match || !e.team_name) return;
    map[e.team_name] = (map[e.team_name] || 0) + (Number(e.calculated_value) || 0);
  });
  Object.keys(adj.backcharges || {}).concat(Object.keys(adj.adicionais || {})).forEach((t) => { if (!(t in map)) map[t] = 0; });
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([name, gross]) => ({ name, gross }));
};

// Admin → Payroll → Relatórios: adicionar/remover backcharge ou adicional de uma equipe em um relatório já salvo
export const ReportBackchargeModal = ({ open, onOpenChange, reports = [], language = 'pt-BR', onChanged }) => {
  const pt = language === 'pt-BR';
  const [reportId, setReportId] = useState('');
  const [team, setTeam] = useState('');
  const [kind, setKind] = useState('backcharge');
  const [adj, setAdj] = useState({ backcharges: {}, adicionais: {} });
  const [form, setForm] = useState({ valor: '', descricao: '' });
  const [busy, setBusy] = useState(false);

  const K = KINDS[kind];
  const report = useMemo(() => reports.find((r) => r.id === reportId), [reports, reportId]);
  const teams = useMemo(() => teamsOf(report, adj), [report, adj]);
  const items = Array.isArray(adj[K.field]?.[team]) ? adj[K.field][team] : [];
  const gross = teams.find((t) => t.name === team)?.gross || 0;
  const bcTotal = sum(adj.backcharges?.[team]);
  const adTotal = sum(adj.adicionais?.[team]);
  const net = gross - bcTotal + adTotal;

  useEffect(() => { if (!open) { setReportId(''); setTeam(''); setKind('backcharge'); setForm({ valor: '', descricao: '' }); } }, [open]);
  useEffect(() => { setAdj({ backcharges: report?.backcharges && typeof report.backcharges === 'object' ? report.backcharges : {}, adicionais: report?.adicionais && typeof report.adicionais === 'object' ? report.adicionais : {} }); setTeam(''); }, [report?.id]);

  const applyResult = (data) => { const next = { backcharges: data.backcharges || {}, adicionais: data.adicionais || {} }; setAdj(next); onChanged?.(reportId, next); };

  const add = async () => {
    const valor = parseFloat(form.valor);
    if (!valor || valor <= 0) { toast.error(pt ? 'Informe um valor válido' : 'Enter a valid amount'); return; }
    if (!form.descricao.trim()) { toast.error(pt ? 'Informe uma descrição' : 'Enter a description'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/payroll/${reportId}/${K.field}`, { team_name: team, valor, descricao: form.descricao.trim() });
      applyResult(data);
      setForm({ valor: '', descricao: '' });
      toast.success(`${pt ? K.pt : K.en} ${pt ? 'de' : 'of'} ${money(valor)} ${pt ? 'aplicado em' : 'applied to'} ${team}`);
    } catch (e) { toast.error(e?.response?.data?.detail || (pt ? 'Erro ao adicionar' : 'Failed to add')); } finally { setBusy(false); }
  };

  const remove = async (it) => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/admin/payroll/${reportId}/${K.field}/${encodeURIComponent(team)}/${encodeURIComponent(it.id)}`);
      applyResult(data);
      toast.success(pt ? `${K.pt} removido` : `${K.en} removed`);
    } catch (e) { toast.error(e?.response?.data?.detail || (pt ? 'Erro ao remover' : 'Failed to remove')); } finally { setBusy(false); }
  };

  const tone = (k) => (k === 'adicional' ? 'green' : 'yellow');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-backcharge-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-800"><AlertTriangle className="h-5 w-5 text-yellow-600" /> {pt ? 'Ajustes do Relatório' : 'Report Adjustments'} <span className="text-xs font-normal text-slate-500">· Backcharge / {pt ? 'Adicional' : 'Additional'}</span></DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <Label className="text-xs font-semibold text-blue-800">📊 {pt ? 'Relatório (semana)' : 'Report (week)'}</Label>
            <select className="mt-1 w-full border rounded px-3 py-2 bg-white text-sm" value={reportId} onChange={(e) => setReportId(e.target.value)} data-testid="backcharge-report-select">
              <option value="">{pt ? 'Selecione um relatório...' : 'Select a report...'}</option>
              {reports.map((r) => <option key={r.id} value={r.id}>{pt ? 'Semana terminada em' : 'Week ended'} {fmtDate(r.week_ending)} · {r.total_entries ?? (r.entries || []).length} {pt ? 'entradas' : 'entries'}</option>)}
            </select>
          </div>

          {report && (
            <div>
              <Label className="text-xs font-semibold text-slate-700">👥 {pt ? 'Equipe' : 'Team'}</Label>
              <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="backcharge-team-list">
                {teams.length === 0 && <p className="col-span-full text-xs text-slate-500">{pt ? 'Nenhuma equipe neste relatório' : 'No teams in this report'}</p>}
                {teams.map((t) => {
                  const tb = sum(adj.backcharges?.[t.name]);
                  const ta = sum(adj.adicionais?.[t.name]);
                  const on = t.name === team;
                  return (
                    <button key={t.name} type="button" onClick={() => setTeam(t.name)} aria-pressed={on} data-testid={`backcharge-team-${t.name}`}
                      className={`text-left rounded-lg border px-3 py-2 transition-colors ${on ? 'border-slate-700 bg-slate-50 ring-2 ring-slate-400' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                      <div className="text-sm font-bold text-slate-900">{t.name}</div>
                      <div className="text-[11px] text-green-700 font-semibold">{money(t.gross - tb + ta)}</div>
                      <div className="flex gap-2 text-[10px]">
                        {tb > 0 && <span className="text-yellow-700">⚠️ -{money(tb)}</span>}
                        {ta > 0 && <span className="text-green-700">💰 +{money(ta)}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {report && team && (
            <div className="space-y-3" data-testid="backcharge-team-panel">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800 flex flex-wrap items-center justify-between gap-2">
                <span className="font-bold">{team}</span>
                <span data-testid="backcharge-team-final">{pt ? 'Valor Final' : 'Final Value'}: {(bcTotal > 0 || adTotal > 0) && <s className="text-slate-500">{money(gross)}</s>} <b className="text-green-700">{money(net)}</b>{bcTotal > 0 && <span className="text-yellow-700"> (−{money(bcTotal)})</span>}{adTotal > 0 && <span className="text-green-700"> (+{money(adTotal)})</span>}</span>
              </div>

              <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1" data-testid="adjust-kind-tabs">
                {Object.entries(KINDS).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => { setKind(k); setForm({ valor: '', descricao: '' }); }} aria-pressed={kind === k} data-testid={`adjust-kind-${k}`}
                    className={`h-9 rounded-md text-xs font-bold transition-colors ${kind === k ? (tone(k) === 'green' ? 'bg-green-600 text-white shadow' : 'bg-yellow-500 text-white shadow') : 'text-slate-600 hover:bg-white'}`}>
                    {v.icon} {pt ? v.pt : v.en} <span className="font-normal opacity-80">({sum(adj[v.field]?.[team]) > 0 ? `${v.sign < 0 ? '−' : '+'}${money(sum(adj[v.field]?.[team]))}` : (pt ? 'nenhum' : 'none')})</span>
                  </button>
                ))}
              </div>

              <div className="rounded-lg border border-slate-200 divide-y" data-testid="backcharge-list">
                {items.length === 0 && <p className="text-xs text-slate-500 p-3">{pt ? `Nenhum ${K.pt.toLowerCase()} nesta equipe.` : `No ${K.en.toLowerCase()} for this team.`}</p>}
                {items.map((it, i) => (
                  <div key={it.id || i} className={`flex items-center gap-3 p-2.5 ${K.tone === 'green' ? 'bg-green-50/60' : 'bg-yellow-50/60'}`} data-testid={`backcharge-row-${it.id || i}`}>
                    <span>{K.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${K.tone === 'green' ? 'text-green-900' : 'text-yellow-900'}`}>{it.descricao}</div>
                      {it.added_by && <div className="text-[10px] text-slate-500">{pt ? 'por' : 'by'} {it.added_by}</div>}
                    </div>
                    <span className={`text-sm font-bold ${K.sign < 0 ? 'text-red-600' : 'text-green-700'}`}>{K.sign < 0 ? '-' : '+'}{money(it.valor)}</span>
                    <button type="button" disabled={busy || !it.id} onClick={() => remove(it)} className="h-8 w-8 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center disabled:opacity-40" title={pt ? 'Remover' : 'Remove'} data-testid={`backcharge-remove-${it.id || i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>

              <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                <Label className="text-xs font-semibold">{pt ? `Novo ${K.pt.toLowerCase()}` : `New ${K.en.toLowerCase()}`}</Label>
                <div className="grid grid-cols-[7rem_1fr] gap-2">
                  <Input type="number" step="0.01" min="0" placeholder="150.00" value={form.valor} onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))} data-testid="backcharge-valor-input" />
                  <Input placeholder={pt ? K.placeholderPt : K.placeholderEn} value={form.descricao} onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))} data-testid="backcharge-descricao-input" />
                </div>
                <Button onClick={add} disabled={busy} className={`w-full ${K.btn}`} data-testid="backcharge-add-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {pt ? `Adicionar ${K.pt}` : `Add ${K.en}`}
                </Button>
                <p className="text-[11px] text-slate-500">{K.sign < 0
                  ? (pt ? `Será descontado do Valor Final de ${team} e aparece no relatório e no PDF da equipe.` : `Deducted from ${team}'s Final Value and shown in the team's report and PDF.`)
                  : (pt ? `Será somado ao Valor Final de ${team} e aparece no relatório e no PDF da equipe.` : `Added to ${team}'s Final Value and shown in the team's report and PDF.`)}</p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportBackchargeModal;
